import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Investment } from '../types/Investment';
import { generateAriaId, prefersReducedMotion } from '../utils/a11y';
import './InvestmentTile.css';


interface InvestmentTileProps {
  investment: Investment;
  isSelected: boolean;
  onSelect: (investment: Investment) => void;
  disabled: boolean;
}

export const InvestmentTile: React.FC<InvestmentTileProps> = React.memo(({
  investment,
  isSelected,
  onSelect,
  disabled
}) => {
  const tileId = generateAriaId(`investment-${investment.id}`);
  const descriptionId = generateAriaId(`desc-${investment.id}`);
  const impactId = generateAriaId(`impact-${investment.id}`);
  const shouldReduceMotion = prefersReducedMotion();
  const handleClick = () => {
    if (!disabled || isSelected) {
      onSelect(investment);
    }
  };



  const tileVariants = {
    initial: { opacity: 0, scale: shouldReduceMotion ? 1 : 0.9 },
    animate: { 
      opacity: 1, 
      scale: 1,
      transition: shouldReduceMotion ? { duration: 0 } : {
        duration: 0.3,
      }
    },
  };

  const selectedBadgeVariants = {
    initial: { scale: shouldReduceMotion ? 1 : 0, rotate: shouldReduceMotion ? 0 : -180 },
    animate: { 
      scale: 1, 
      rotate: 0,
      transition: shouldReduceMotion ? { duration: 0 } : {
        type: "spring" as const,
        stiffness: 500,
        damping: 15,
      }
    },
    exit: { 
      scale: shouldReduceMotion ? 1 : 0, 
      rotate: shouldReduceMotion ? 0 : 180,
      transition: shouldReduceMotion ? { duration: 0 } : {
        duration: 0.2,
      }
    }
  };

  return (
    <motion.div 
      className={`investment-tile ${isSelected ? 'selected' : ''} ${disabled && !isSelected ? 'disabled' : ''}`}
      onClick={handleClick}
      variants={tileVariants}
      initial="initial"
      animate="animate"
      whileHover={(!disabled || isSelected) && !shouldReduceMotion ? { 
        scale: 1.03,
        boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
        transition: { duration: 0.2 }
      } : {}}
      whileTap={(!disabled || isSelected) && !shouldReduceMotion ? { scale: 0.98 } : {}}
      role="button"
      tabIndex={disabled && !isSelected ? -1 : 0}
      aria-pressed={isSelected}
      aria-disabled={disabled && !isSelected}
      aria-label={`${investment.name}`}
      aria-describedby={`${descriptionId} ${impactId}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <div className="tile-header">
        <motion.span 
          className="tile-icon"
          animate={isSelected && !shouldReduceMotion ? { scale: [1, 1.2, 1] } : {}}
          transition={{ duration: shouldReduceMotion ? 0 : 0.3 }}
          aria-hidden="true"
        >
          {investment.icon}
        </motion.span>
        <h3 className="tile-name" id={tileId}>{investment.name}</h3>
      </div>
      
      <p className="tile-description" id={descriptionId}>{investment.description}</p>
      
      
      <div className="tile-impact" id={impactId}>
        <h4 className="impact-title">Expected Outcomes:</h4>
        <div className="outcome-list" role="list" aria-label="Investment outcomes">
          {(investment.businessOutcomes || [
            'Enhanced organizational security',
            'Improved risk management',
            'Increased operational resilience'
          ]).map((outcome, index) => (
            <motion.div 
              key={index}
              className="outcome-item"
              whileHover={!shouldReduceMotion ? { scale: 1.02 } : {}}
              transition={{ duration: shouldReduceMotion ? 0 : 0.2 }}
              role="listitem"
            >
              <span className="outcome-text">{outcome}</span>
            </motion.div>
          ))}
        </div>
      </div>
      
      <AnimatePresence>
        {isSelected && (
          <motion.div 
            className="selected-badge"
            variants={selectedBadgeVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            aria-hidden="true"
          >
            ✓ Selected
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for better performance
  // Only re-render if investment data or selection state changes
  return (
    prevProps.investment.id === nextProps.investment.id &&
    prevProps.investment.name === nextProps.investment.name &&
    prevProps.investment.description === nextProps.investment.description &&
    prevProps.investment.icon === nextProps.investment.icon &&
    prevProps.investment.cost === nextProps.investment.cost &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.onSelect === nextProps.onSelect &&
    // Deep compare business outcomes array
    JSON.stringify(prevProps.investment.businessOutcomes) === JSON.stringify(nextProps.investment.businessOutcomes)
  );
});

InvestmentTile.displayName = 'InvestmentTile';